import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Truck, LogOut, Menu, X, UserCog } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const NAV_BY_ROLE = {
  admin: [
    { to: "/admin", label: "Dashboard" },
    { to: "/admin/pedidos", label: "Pedidos" },
    { to: "/admin/bodega", label: "Bodega" },
    { to: "/admin/leads", label: "Leads" },
    { to: "/admin/clientes", label: "Clientes" },
    { to: "/admin/repartidores", label: "Repartidores" },
    { to: "/admin/reportes", label: "Reportes" },
    { to: "/admin/geocache", label: "Geocache" },
    { to: "/admin/config", label: "Config" },
  ],
  cliente: [
    { to: "/cliente", label: "Dashboard" },
    { to: "/cliente/pedidos", label: "Mis Pedidos" },
    { to: "/cliente/nuevo", label: "Nuevo Pedido" },
    { to: "/cliente/masivo", label: "Carga Masiva" },
    { to: "/cliente/bultos", label: "Bultos" },
    { to: "/cliente/reportes", label: "Reportes" },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pendingLeads, setPendingLeads] = useState(0);
  const nav = useNavigate();
  const items = NAV_BY_ROLE[user?.role] || [];

  // Polling de leads pendientes (admin only) cada 60s
  useEffect(() => {
    if (user?.role !== "admin") return;
    let mounted = true;
    const load = () => api.get("/dashboard/admin")
      .then((r) => { if (mounted) setPendingLeads(r.data.pending_leads || 0); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, [user?.role]);

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col bg-noise">
      <header className="border-b border-white/10 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to={`/${user?.role || ""}`} className="flex items-center gap-3 group" data-testid="brand-link">
              <img src="/img/voycargo-logo.png" alt="VoyCargo Express" className="w-10 h-10 object-contain" />
              <span className="font-mono-tech text-[10px] px-1.5 py-0.5 bg-white/10 border border-white/20">
                {user?.role?.toUpperCase()}
              </span>
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === `/${user?.role}`}
                  data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `relative px-3 py-2 text-sm font-medium border ${
                      isActive
                        ? "border-yellow-300/50 bg-yellow-300/10 text-yellow-300"
                        : "border-transparent text-zinc-400 hover:text-white hover:border-white/20"
                    }`
                  }
                >
                  {it.label}
                  {it.label === "Leads" && pendingLeads > 0 && (
                    <span
                      data-testid="leads-badge"
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-yellow-300 text-black text-[10px] font-mono-tech font-bold flex items-center justify-center rounded-full"
                    >
                      {pendingLeads > 99 ? "99+" : pendingLeads}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-medium">{user?.name}</span>
              <span className="font-mono-tech text-[10px] text-zinc-500">{user?.email}</span>
            </div>
            <Link
              to="/perfil"
              data-testid="perfil-btn"
              className="w-9 h-9 border border-white/10 hover:border-yellow-300/40 hover:bg-yellow-300/10 hover:text-yellow-300 flex items-center justify-center transition-colors"
              title="Perfil / Cambiar contraseña"
            >
              <UserCog className="w-4 h-4" />
            </Link>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="w-9 h-9 border border-white/10 hover:border-yellow-300/40 hover:bg-yellow-300/10 hover:text-yellow-300 flex items-center justify-center transition-colors"
              title="Cerrar sesión"
            >
              <LogOut className="w-4 h-4" />
            </button>
            <button
              className="md:hidden w-9 h-9 border border-white/10 flex items-center justify-center"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-white/10 bg-zinc-950">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-6 py-3 text-sm border-b border-white/5 ${
                    isActive ? "text-yellow-300 bg-yellow-300/5" : "text-zinc-300"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </div>
        )}
      </header>
      <main className="flex-1 max-w-[1600px] w-full mx-auto px-6 py-8">{children}</main>
      <footer className="border-t border-white/10 py-4">
        <div className="max-w-[1600px] mx-auto px-6 flex items-center justify-between text-xs text-zinc-500 font-mono-tech">
          <span>VOYCARGO EXPRESS © 2026</span>
          <span>Ultima milla · Chile</span>
        </div>
      </footer>
    </div>
  );
}
